import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractDraftingService from '../../app/services/hooks/useContractDraftingService';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import { AppContext } from '../../contexts/AppContext';

interface SignatureInvitation {
    id: number;
    signer_name: string;
    signer_email: string;
    status: string;
    signed_at: string | null;
    sent_at: string | null;
    sign_url: string | null;
}

interface ContractType {
    id: number;
    name: string;
    price: number;
    has_articles: boolean;
    signatures_required: number;
    is_signature_only: boolean;
}

interface RequestDetail {
    id: number;
    reference_code: string;
    contract_type: ContractType;
    payment_status: string;
    payment_amount: string;
    payment_date: string;
    flow_order_id: string;
    status: string;
    signatures_required: number;
    signatures_completed: number;
    signature_status: string;
    signature_invitations: SignatureInvitation[];
    can_skip_payment: boolean;
    has_active_membership: boolean;
    is_admin: boolean;
}

interface Signer {
    email: string;
    name: string;
    rut?: string;
}

const PaymentSuccess: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { 
        getRequestDetail, 
        downloadFinalDocument, 
        addSigners,
        sendSignatureInvitations,
        deleteSigner,
        fetchingRequestDetail,
        fetchingAddSigners,
        fetchingSendInvitations,
        fetchingDeleteSigner
    } = useContractDraftingService();
    
    const [request, setRequest] = useState<RequestDetail | null>(null);
    const [signers, setSigners] = useState<Signer[]>([{ email: '', name: '', rut: '' }]);
    const [showAddSignersForm, setShowAddSignersForm] = useState(false);
    const [customMessage, setCustomMessage] = useState('');

    useEffect(() => {
        loadRequestDetail();
    }, []);

    useEffect(() => {
        if (fetchingRequestDetail || fetchingAddSigners || fetchingSendInvitations || fetchingDeleteSigner) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingRequestDetail, fetchingAddSigners, fetchingSendInvitations, fetchingDeleteSigner]);

    const loadRequestDetail = () => {
        getRequestDetail(parseInt(id), {
            onSuccess: (res) => {
                setRequest(res.data.request);
            },
            onError: (res) => {
                toast.error(res.message || 'Error al cargar los datos');
            }
        });
    };

    const handleDownloadFinal = () => {
        downloadFinalDocument(parseInt(id), request?.reference_code);
    };

    const addSignerRow = () => {
        if (signers.length < 10) {
            setSigners([...signers, { email: '', name: '', rut: '' }]);
        }
    };

    const removeSignerRow = (index: number) => {
        if (signers.length > 1) {
            const newSigners = signers.filter((_, i) => i !== index);
            setSigners(newSigners);
        }
    };

    const updateSigner = (index: number, field: keyof Signer, value: string) => {
        const newSigners = [...signers];
        newSigners[index] = { ...newSigners[index], [field]: value };
        setSigners(newSigners);
    };

    const handleAddSigners = () => {
        const filled = signers.filter(s => s.email.trim() || s.name.trim());
        if (filled.some(s => !s.email.trim() || !s.name.trim())) {
            toast.error('Completa el nombre y el correo de cada firmante (o quita la fila).');
            return;
        }
        if (filled.some(s => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim()))) {
            toast.error('Revisa el correo de los firmantes.');
            return;
        }
        const validSigners = filled;

        if (validSigners.length === 0) {
            toast.error('Debe agregar al menos un firmante con email y nombre');
            return;
        }

        addSigners(parseInt(id), validSigners, {
            onSuccess: (res) => {
                toast.success(res.message || 'Firmantes agregados exitosamente');
                setShowAddSignersForm(false);
                setSigners([{ email: '', name: '', rut: '' }]);
                
                // Invitar solo a los recién agregados (antes se reenviaba a todos los pendientes)
                const newIds = (res.data?.invitations || []).map((inv: any) => inv.id).filter(Boolean);
                sendSignatureInvitations(parseInt(id), '', newIds, {
                    onSuccess: (invRes) => {
                        toast.success('Invitaciones enviadas por email');
                        loadRequestDetail();
                    },
                    onError: (invErr) => {
                        toast.warning('Firmantes guardados. Las invitaciones se pueden enviar manualmente.');
                        loadRequestDetail();
                    }
                });
            },
            onError: (res) => {
                toast.error(res.message || 'Error al agregar firmantes');
            }
        });
    };

    const handleSendInvitations = () => {
        sendSignatureInvitations(parseInt(id), customMessage, [], {
            onSuccess: (res) => {
                toast.success(res.message || 'Invitaciones enviadas exitosamente');
                setCustomMessage('');
                loadRequestDetail();
            },
            onError: (res) => {
                toast.error(res.message || 'Error al enviar invitaciones');
            }
        });
    };

    const handleDeleteSigner = (signerId: number, signerName: string) => {
        if (window.confirm(`¿Estás seguro de eliminar a "${signerName}" de los firmantes?`)) {
            deleteSigner(parseInt(id), signerId, {
                onSuccess: (res) => {
                    toast.success(res.message || 'Firmante eliminado exitosamente');
                    loadRequestDetail();
                },
                onError: (res) => {
                    toast.error(res.message || 'Error al eliminar firmante');
                }
            });
        }
    };

    const isPaid = request?.payment_status === 'paid';
    // Un documento "solo firmas" existe justamente para recolectar firmas, así que
    // siempre ofrece el panel de firmantes aunque el tipo tenga el contador en cero.
    const requiresSignatures = (request?.contract_type?.signatures_required || 0) > 0
        || Boolean(request?.contract_type?.is_signature_only);
    const hasInvitations = (request?.signature_invitations?.length || 0) > 0;
    const pendingInvitations = request?.signature_invitations?.filter(i => i.status === 'pending' || i.status === 'sent') || [];

    const getInvitationStatusBadge = (status: string) => {
        const statusMap: Record<string, { label: string; class: string }> = {
            'pending': { label: 'Pendiente', class: 'bg-warning text-dark' },
            'sent': { label: 'Enviado', class: 'bg-info text-white' },
            'viewed': { label: 'Visto', class: 'bg-primary text-white' },
            'signed': { label: 'Firmado', class: 'bg-success text-white' },
            'rejected': { label: 'Rechazado', class: 'bg-danger text-white' },
            'expired': { label: 'Expirado', class: 'bg-secondary text-white' },
        };
        const info = statusMap[status] || { label: status, class: 'bg-secondary text-white' };
        return <span className={`badge ${info.class}`}>{info.label}</span>;
    };

    // Estilos inline para inputs limpios
    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '10px 14px',
        border: '1px solid #dee2e6',
        borderRadius: '6px',
        fontSize: '14px',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        backgroundColor: '#ffffff',
        outline: 'none'
    };

    const labelStyle: React.CSSProperties = {
        display: 'block',
        marginBottom: '6px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#495057'
    };

    return (
        <div className="container-fluid py-4">
            <div className="row justify-content-center">
                <div className="col-lg-9 col-xl-8">
                    {isPaid ? (
                        <>
                            {/* Header de Éxito */}
                            <div className="text-center mb-4">
                                <div
                                    className="d-inline-flex align-items-center justify-content-center mb-3"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                                        color: 'white',
                                        fontSize: '40px',
                                        boxShadow: '0 4px 15px rgba(40, 167, 69, 0.3)'
                                    }}
                                >
                                    <i className="fas fa-check"></i>
                                </div>
                                <h2 style={{ color: '#001849', fontWeight: 600 }}>Contrato Listo</h2>
                                <p className="text-muted mb-0">
                                    Tu contrato está disponible para descargar
                                    {requiresSignatures && ' y enviar a firmar'}
                                </p>
                            </div>

                            {/* Información del Contrato */}
                            <div className="card border-0 shadow-sm mb-4">
                                <div className="card-body p-4">
                                    <div className="row">
                                        <div className="col-sm-6 mb-3 mb-sm-0">
                                            <span className="text-muted small d-block mb-1">Código de Referencia</span>
                                            <span className="fw-bold" style={{ color: '#001849' }}>{request?.reference_code}</span>
                                        </div>
                                        <div className="col-sm-6">
                                            <span className="text-muted small d-block mb-1">Tipo de Contrato</span>
                                            <span>{request?.contract_type?.name}</span>
                                        </div>
                                        {!!request?.payment_amount && parseFloat(request.payment_amount) > 0 && (
                                            <>
                                                <div className="col-sm-6 mt-3">
                                                    <span className="text-muted small d-block mb-1">Monto Pagado</span>
                                                    <span className="fw-bold text-success">
                                                        ${parseInt(request.payment_amount).toLocaleString('es-CL')} CLP
                                                    </span>
                                                </div>
                                                <div className="col-sm-6 mt-3">
                                                    <span className="text-muted small d-block mb-1">Fecha</span>
                                                    <span>
                                                        {request?.payment_date 
                                                            ? new Date(request.payment_date).toLocaleDateString('es-CL')
                                                            : '-'
                                                        }
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Card de Descarga - Compacta */}
                            <div 
                                className="card border-0 mb-4"
                                style={{ 
                                    background: 'linear-gradient(135deg, #001849 0%, #1a3a6e 100%)',
                                    borderRadius: '10px'
                                }}
                            >
                                <div className="card-body py-3 px-4">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                                        <div className="d-flex align-items-center text-white">
                                            <div 
                                                className="d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: '45px',
                                                    height: '45px',
                                                    borderRadius: '8px',
                                                    backgroundColor: 'rgba(255,255,255,0.15)'
                                                }}
                                            >
                                                <i className="fas fa-file-pdf" style={{ fontSize: '20px' }}></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0">Documento Final</h6>
                                                <small style={{ opacity: 0.8 }}>PDF</small>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-light px-4"
                                            onClick={handleDownloadFinal}
                                            style={{ 
                                                fontWeight: 500,
                                                borderRadius: '6px',
                                                color: '#001849'
                                            }}
                                        >
                                            <i className="fas fa-download me-2"></i>
                                            Descargar
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Sección de Firmas */}
                            {requiresSignatures && (
                                <div className="card border-0 shadow-sm mb-4">
                                    <div 
                                        className="card-header border-0 py-3 px-4"
                                        style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}
                                    >
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="d-flex align-items-center justify-content-center me-3"
                                                style={{
                                                    width: '40px',
                                                    height: '40px',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#001849',
                                                    color: 'white'
                                                }}
                                            >
                                                <i className="fas fa-signature"></i>
                                            </div>
                                            <div>
                                                <h6 className="mb-0" style={{ color: '#001849' }}>Firmas del Contrato</h6>
                                                <small className="text-muted">
                                                    Este contrato requiere {request?.contract_type?.signatures_required} firma(s)
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body p-4">
                                        {/* Lista de firmantes existentes */}
                                        {hasInvitations && (
                                            <div className="mb-4">
                                                <h6 className="mb-3" style={{ color: '#001849' }}>
                                                    Firmantes agregados
                                                </h6>
                                                <div className="table-responsive">
                                                    <table className="table table-hover mb-0">
                                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                            <tr>
                                                                <th style={{ fontWeight: 500, color: '#495057' }}>Nombre</th>
                                                                <th style={{ fontWeight: 500, color: '#495057' }}>Email</th>
                                                                <th style={{ fontWeight: 500, color: '#495057' }}>Estado</th>
                                                                <th style={{ fontWeight: 500, color: '#495057' }}>Fecha</th>
                                                                <th style={{ fontWeight: 500, color: '#495057', width: '110px' }}>Acciones</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {request?.signature_invitations?.map((inv) => (
                                                                <tr key={inv.id}>
                                                                    <td>{inv.signer_name}</td>
                                                                    <td className="text-muted">{inv.signer_email}</td>
                                                                    <td>{getInvitationStatusBadge(inv.status)}</td>
                                                                    <td className="text-muted">
                                                                        {inv.signed_at 
                                                                            ? new Date(inv.signed_at).toLocaleDateString('es-CL')
                                                                            : '-'
                                                                        }
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex gap-1">
                                                                            {inv.sign_url && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-primary"
                                                                                    onClick={() => { window.location.href = inv.sign_url as string; }}
                                                                                    title="Estás en la lista de firmantes: firmar ahora"
                                                                                    style={{ padding: '4px 8px' }}
                                                                                >
                                                                                    <i className="fas fa-pen-nib me-1"></i>Firmar
                                                                                </button>
                                                                            )}
                                                                            {inv.status !== 'signed' && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-sm btn-outline-danger"
                                                                                    onClick={() => handleDeleteSigner(inv.id, inv.signer_name)}
                                                                                    disabled={fetchingDeleteSigner}
                                                                                    title="Eliminar firmante"
                                                                                    style={{ padding: '4px 8px' }}
                                                                                >
                                                                                    <i className="fas fa-trash-alt"></i>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                                
                                                {/* Enviar invitaciones */}
                                                {pendingInvitations.length > 0 && (
                                                    <div 
                                                        className="mt-4 p-3 rounded"
                                                        style={{ backgroundColor: '#f0f7ff', border: '1px solid #cce5ff' }}
                                                    >
                                                        <div className="mb-3">
                                                            <label style={labelStyle}>
                                                                Mensaje personalizado (opcional)
                                                            </label>
                                                            <input
                                                                type="text"
                                                                style={inputStyle}
                                                                value={customMessage}
                                                                onChange={(e) => setCustomMessage(e.target.value)}
                                                                placeholder="Ej: Por favor firme el contrato antes del viernes"
                                                                onFocus={(e) => e.target.style.borderColor = '#001849'}
                                                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                                            />
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="btn"
                                                            onClick={handleSendInvitations}
                                                            disabled={fetchingSendInvitations}
                                                            style={{
                                                                backgroundColor: '#001849',
                                                                color: 'white',
                                                                fontWeight: 500,
                                                                padding: '10px 20px',
                                                                borderRadius: '6px'
                                                            }}
                                                        >
                                                            <i className="fas fa-paper-plane me-2"></i>
                                                            Enviar invitaciones ({pendingInvitations.length})
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Formulario para agregar firmantes */}
                                        {!showAddSignersForm ? (
                                            <button
                                                type="button"
                                                className="btn btn-outline-primary"
                                                onClick={() => setShowAddSignersForm(true)}
                                                style={{ 
                                                    borderColor: '#001849', 
                                                    color: '#001849',
                                                    fontWeight: 500
                                                }}
                                            >
                                                <i className="fas fa-user-plus me-2"></i>
                                                Agregar firmantes
                                            </button>
                                        ) : (
                                            <div 
                                                className="p-4 rounded"
                                                style={{ 
                                                    backgroundColor: '#f8f9fa',
                                                    border: '1px solid #e9ecef'
                                                }}
                                            >
                                                <h6 className="mb-2" style={{ color: '#001849' }}>
                                                    Agregar firmantes
                                                </h6>
                                                <p className="text-muted small mb-4">
                                                    Ingrese los datos de las personas que deben firmar. 
                                                    Recibirán un email con el enlace para firmar.
                                                </p>
                                                
                                                {signers.map((signer, index) => (
                                                    <div 
                                                        key={index} 
                                                        className="row g-3 mb-3 align-items-end"
                                                    >
                                                        <div className="col-md-4">
                                                            {index === 0 && (
                                                                <label style={labelStyle}>
                                                                    Nombre completo <span className="text-danger">*</span>
                                                                </label>
                                                            )}
                                                            <input
                                                                type="text"
                                                                style={inputStyle}
                                                                value={signer.name}
                                                                onChange={(e) => updateSigner(index, 'name', e.target.value)}
                                                                placeholder="Juan Pérez"
                                                                onFocus={(e) => e.target.style.borderColor = '#001849'}
                                                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                                            />
                                                        </div>
                                                        <div className="col-md-4">
                                                            {index === 0 && (
                                                                <label style={labelStyle}>
                                                                    Email <span className="text-danger">*</span>
                                                                </label>
                                                            )}
                                                            <input
                                                                type="email"
                                                                style={inputStyle}
                                                                value={signer.email}
                                                                onChange={(e) => updateSigner(index, 'email', e.target.value)}
                                                                placeholder="email@ejemplo.com"
                                                                onFocus={(e) => e.target.style.borderColor = '#001849'}
                                                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                                            />
                                                        </div>
                                                        <div className="col-md-3">
                                                            {index === 0 && (
                                                                <label style={labelStyle}>RUT (opcional)</label>
                                                            )}
                                                            <input
                                                                type="text"
                                                                style={inputStyle}
                                                                value={signer.rut || ''}
                                                                onChange={(e) => updateSigner(index, 'rut', e.target.value)}
                                                                placeholder="12.345.678-9"
                                                                onFocus={(e) => e.target.style.borderColor = '#001849'}
                                                                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
                                                            />
                                                        </div>
                                                        <div className="col-md-1">
                                                            {signers.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-outline-danger btn-sm"
                                                                    onClick={() => removeSignerRow(index)}
                                                                    style={{ padding: '8px 12px' }}
                                                                >
                                                                    <i className="fas fa-times"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                                
                                                <div className="d-flex gap-2 mt-4 pt-2">
                                                    {signers.length < 10 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary"
                                                            onClick={addSignerRow}
                                                        >
                                                            <i className="fas fa-plus me-1"></i> 
                                                            Agregar otro
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn"
                                                        onClick={handleAddSigners}
                                                        disabled={fetchingAddSigners || fetchingSendInvitations}
                                                        style={{
                                                            backgroundColor: '#001849',
                                                            color: 'white',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        <i className="fas fa-paper-plane me-2"></i>
                                                        {fetchingAddSigners || fetchingSendInvitations ? 'Enviando...' : 'Guardar y enviar invitaciones'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-light"
                                                        onClick={() => {
                                                            setShowAddSignersForm(false);
                                                            setSigners([{ email: '', name: '', rut: '' }]);
                                                        }}
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Recomendaciones */}
                            <div className="card border-0 shadow-sm">
                                <div className="card-body p-4">
                                    <h6 className="mb-3" style={{ color: '#001849' }}>
                                        <i className="fas fa-lightbulb me-2 text-warning"></i>
                                        Recomendaciones
                                    </h6>
                                    <ul className="mb-0 ps-3" style={{ lineHeight: '1.8' }}>
                                        <li>Guarda una copia de seguridad del documento</li>
                                        <li>Revisa el contenido antes de firmar</li>
                                        {requiresSignatures && (
                                            <li>Los firmantes recibirán un email con el enlace para firmar</li>
                                        )}
                                        <li>El documento estará disponible en tu panel por tiempo indefinido</li>
                                    </ul>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Pago pendiente */
                        <div className="card border-0 shadow-sm">
                            <div className="card-body p-5 text-center">
                                <div
                                    className="d-inline-flex align-items-center justify-content-center mb-4"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                                        color: 'white',
                                        fontSize: '36px'
                                    }}
                                >
                                    <i className="fas fa-clock"></i>
                                </div>
                                <h3 className="mb-3" style={{ color: '#001849' }}>Pago en Proceso</h3>
                                <p className="text-muted mb-0">
                                    Estamos verificando tu pago. Recibirás un email cuando el documento esté disponible.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Botón para volver */}
                    <div className="text-center mt-4">
                        <ButtonGeneral
                            type="button"
                            variant="outline"
                            title="Ir a Mis Contratos"
                            onClick={() => history.push('/intranet/contracts')}
                            icon="fa-arrow-left"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;
